import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { waLink } from "@/lib/contacts";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, ShieldCheck, Lightning,
  CheckCircle, Camera, House, WhatsappLogo, ImageSquare, ArrowClockwise, X,
} from "@phosphor-icons/react";
import QLogo from "@/components/QLogo";
import WizardFooter from "@/components/WizardFooter";
import SearchableDropdown from "@/components/SearchableDropdown";
import { buildAttributionSubmitPayload, clearAttribution } from "@/lib/attribution";

// Manual-only flow — Vahan removed. 7 steps now.
const STEPS = 7;
const FUELS = ["Petrol", "Diesel", "CNG", "Electric", "Hybrid"];
const TRANS = ["Manual", "Automatic"];
// Frontend Seller Form Improvements (v2): canonical brand list — searchable dropdown
// reduces spelling variance ("Hyndai", "Hundai") that bloated the admin CRM.
const BRANDS = [
  "Maruti Suzuki", "Hyundai", "Tata", "Mahindra", "Kia", "Toyota", "Honda", "MG", "Renault",
  "Nissan", "Volkswagen", "Skoda", "Ford", "Jeep", "BMW", "Mercedes-Benz", "Audi", "Volvo", "Other",
];

// Canonical fallback option values. Stored to vehicle docs verbatim so
// downstream analytics + normalization can detect them with a single check.
const MODEL_UNKNOWN_VALUE = "Other / Not Sure";
const VARIANT_UNKNOWN_VALUES = {
  IDK: "I don't know / Not sure",
  OTHER: "Other Variant",
};
// Year dropdown: current year down to 1990.
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 1990 + 1 }, (_, i) => CURRENT_YEAR - i);
const URGENCY = [
  { v: "immediate", label: "Immediate" },
  { v: "this_week", label: "This Week" },
  { v: "this_month", label: "This Month" },
  { v: "exploring", label: "Exploring" },
];

// Customer intent — replaces the older "urgency" question on the Your Goal step.
// Drives lead scoring + acquisition prioritization downstream. 'other' unlocks
// an optional free-text field (max 100 chars) — never mandatory.
const CUSTOMER_INTENTS = [
  { v: "get_an_offer",          label: "Get an Offer" },
  { v: "sell_quickly",          label: "Sell Quickly" },
  { v: "sell_at_best_price",    label: "Sell at the Best Possible Price" },
  { v: "upgrade_vehicle",       label: "Upgrade to Another Vehicle" },
  { v: "understand_market",     label: "Understand Current Market Value" },
  { v: "no_longer_needed",      label: "Vehicle No Longer Needed" },
  { v: "other",                 label: "Other" },
];
const CUSTOMER_INTENT_NOTE_MAX = 100;
const OWNER_OPTIONS = ["1", "2", "3", "4+"];
const HYPO_OPTIONS = [{ v: "no", l: "No" }, { v: "yes", l: "Yes" }];
const ACCIDENT_OPTIONS = [{ v: "none", l: "None" }, { v: "minor", l: "Minor" }, { v: "major", l: "Major" }];
const SERVICE_OPTIONS = [{ v: "regular", l: "Regular" }, { v: "partial", l: "Partial" }, { v: "none", l: "None" }];

const DRAFT_KEY = "qd_sell_draft_v2";

function StepHeader({ step, onBack, onHome }) {
  return (
    <div className="flex items-center justify-between mb-7">
      <button data-testid="sell-back-btn" onClick={onBack} className="text-white/55 hover:text-white flex items-center gap-1.5 text-xs">
        <ArrowLeft size={14} /> Back
      </button>
      <div className="flex-1 mx-4">
        <div className="h-[2px] bg-[#1F1F1F] relative overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-[#C20505]" style={{ width: `${(step / STEPS) * 100}%`, transition: "width 0.4s ease" }} />
        </div>
        <div className="text-[10px] text-white/35 mt-1.5">Step {step} of {STEPS}</div>
      </div>
      <button onClick={onHome} className="text-white/55 hover:text-white"><House size={16} /></button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RegistrationPicker — Telangana RTO prefix selection
//
// Two-tab toggle (Hyderabad / Other District) drives which chip set is shown.
// Tapping a chip prefills 'TS09 ' into the registration input so the seller
// only types the AB-1234 portion. Default region = Hyderabad (priority zone)
// so the most common case is zero extra taps.
//
// Lists come from /api/registration/prefixes (file-driven on the backend) —
// adding a district there auto-surfaces here on next page load.
// ─────────────────────────────────────────────────────────────────────────────
function RegistrationPicker({ region, registrationNumber, onChangeRegion, onChangeRegistration }) {
  const [prefixes, setPrefixes] = React.useState({ priority: [], district: [] });

  React.useEffect(() => {
    api.get("/registration/prefixes")
      .then((r) => setPrefixes(r.data || { priority: [], district: [] }))
      .catch(() => {
        // Non-fatal — user can still type freely.
      });
  }, []);

  const list = region === "district" ? prefixes.district : prefixes.priority;

  // When the user taps a chip we prefill 'TS09 ' so they continue typing
  // the rest. Preserves any digits/letters already past the prefix.
  function applyPrefix(p) {
    const rest = (registrationNumber || "").replace(/^[A-Z]{2}\d{1,2}\s*/i, "").trim();
    onChangeRegistration(`${p} ${rest}`.toUpperCase());
  }

  return (
    <div className="mb-6" data-testid="registration-picker">
      <div className="text-[11px] uppercase tracking-widest text-white/55 mb-2 flex items-baseline justify-between">
        <span>Registration Region</span>
        <span className="text-[10px] text-white/30 normal-case tracking-normal">Tap a code to auto-fill</span>
      </div>

      {/* Hyderabad vs District toggle */}
      <div className="inline-flex w-full border border-[#2A2A2A] bg-[#0E0E0E] mb-3" role="tablist">
        <button
          type="button"
          data-testid="region-hyderabad"
          onClick={() => onChangeRegion("hyderabad")}
          className={`flex-1 px-3 py-2.5 text-[11px] font-mono-data tracking-widest uppercase border-r border-[#2A2A2A] transition-colors ${
            region === "hyderabad" ? "bg-[#FF1E1E]/15 text-[#FF1E1E]" : "text-white/55 hover:text-white"
          }`}
        >
          Hyderabad / Greater Hyderabad
        </button>
        <button
          type="button"
          data-testid="region-district"
          onClick={() => onChangeRegion("district")}
          className={`flex-1 px-3 py-2.5 text-[11px] font-mono-data tracking-widest uppercase transition-colors ${
            region === "district" ? "bg-[#FF1E1E]/15 text-[#FF1E1E]" : "text-white/55 hover:text-white"
          }`}
        >
          Other Telangana District
        </button>
      </div>

      {/* Prefix chips — horizontal scroll on small screens */}
      {list.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`prefix-chips-${region}`}>
          {list.map((p) => {
            const isActive = (registrationNumber || "").toUpperCase().startsWith(p.prefix);
            return (
              <button
                type="button"
                key={p.prefix}
                data-testid={`prefix-chip-${p.prefix}`}
                onClick={() => applyPrefix(p.prefix)}
                title={p.district}
                className={`px-2.5 py-1.5 text-[11px] font-mono-data tracking-widest uppercase border transition-colors ${
                  isActive
                    ? "bg-[#FF1E1E] text-white border-[#FF1E1E]"
                    : "border-[#2A2A2A] text-white/65 hover:border-[#FF1E1E]/40 hover:text-white"
                }`}
              >
                {p.prefix}
              </button>
            );
          })}
        </div>
      )}

      {/* Registration number input — kept free-text so no lead is lost. */}
      <FieldRow label="Registration Number (optional)">
        <input
          data-testid="reg-input"
          className="qd-input-block"
          placeholder="TS09 AB 1234"
          value={registrationNumber}
          onChange={(e) => onChangeRegistration(e.target.value.toUpperCase())}
        />
      </FieldRow>
    </div>
  );
}


const DEFAULT_FORM = {
  registration_number: "",
  // Telangana RTO region selector — drives the prefix chips. Default to
  // Hyderabad since that's our priority acquisition zone — minimum taps for
  // the most common case.
  registration_region: "hyderabad",
  make: "", model: "", variant: "", fuel_type: "Petrol",
  year: "",
  transmission: "Manual", km_driven: "", owners: 1, hypothecation: false,
  accident_history: "none", service_history: "regular", photos: [], condition_score: 80,
  expected_price: 0,
  // Customer intent — drives lead scoring/acquisition priority. 'other' opens
  // an optional 100-char note field; both are stored on the lead.
  customer_intent: "get_an_offer",
  customer_intent_note: "",
};

export default function Sell() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(() => {
    // Restore in-progress draft so refresh / nav-back doesn't lose photos or fields.
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft && typeof draft === "object") {
          // Coerce legacy numeric fields back to string (older drafts stored them as numbers).
          const yearStr = draft.year == null || draft.year === "" ? "" : String(draft.year);
          const kmStr = draft.km_driven == null || draft.km_driven === "" ? "" : String(draft.km_driven);
          return { ...DEFAULT_FORM, ...draft, year: yearStr, km_driven: kmStr };
        }
      }
    } catch { /* ignore */ }
    return DEFAULT_FORM;
  });
  const [vid, setVid] = useState(null);
  // Vehicle master (make → model → variants tree) — fetched once on mount.
  // Falls back to empty list if endpoint fails so the form still functions
  // (model becomes a free-text fallback in that degraded path).
  const [vehicleMaster, setVehicleMaster] = useState({ makes: [] });
  useEffect(() => {
    api.get("/vehicle-master")
      .then((r) => setVehicleMaster(r.data || { makes: [] }))
      .catch(() => { /* non-fatal — fallback dropdowns gracefully empty */ });
  }, []);

  // Models / variants for currently-selected make. Sorted by displayOrder.
  // Appends the canonical fallback option at the end so the UI always has
  // an "Other / Not Sure" escape hatch.
  const makeEntry = useMemo(
    () => (vehicleMaster.makes || []).find((m) => m.make === form.make),
    [vehicleMaster, form.make]
  );
  const modelOptions = useMemo(() => {
    if (!makeEntry) return [];
    return [
      ...makeEntry.models.map((m) => ({ value: m.model, label: m.model })),
      { value: MODEL_UNKNOWN_VALUE, label: MODEL_UNKNOWN_VALUE },
    ];
  }, [makeEntry]);
  const variantOptions = useMemo(() => {
    if (!makeEntry || !form.model) return [];
    const m = makeEntry.models.find((x) => x.model === form.model);
    const list = m ? m.variants.map((v) => ({ value: v, label: v })) : [];
    return [
      ...list,
      { value: VARIANT_UNKNOWN_VALUES.IDK, label: VARIANT_UNKNOWN_VALUES.IDK },
      { value: VARIANT_UNKNOWN_VALUES.OTHER, label: VARIANT_UNKNOWN_VALUES.OTHER },
    ];
  }, [makeEntry, form.model]);

  // Dependency-cascade clears: changing brand wipes model + variant; changing
  // model wipes variant. Also re-derives unknown flags + selection source.
  function setMake(v) {
    setForm((p) => ({
      ...p, make: v, model: "", variant: "",
      model_unknown: false, variant_unknown: false,
      vehicle_selection_source: "dropdown",
    }));
  }
  function setModel(v) {
    const isUnknown = v === MODEL_UNKNOWN_VALUE;
    setForm((p) => ({
      ...p, model: v, variant: "",
      model_unknown: isUnknown,
      variant_unknown: false,
      vehicle_selection_source: isUnknown ? "fallback_model_unknown" : "dropdown",
    }));
  }
  function setVariant(v) {
    const isIdk = v === VARIANT_UNKNOWN_VALUES.IDK;
    const isOther = v === VARIANT_UNKNOWN_VALUES.OTHER;
    setForm((p) => ({
      ...p, variant: v,
      variant_unknown: isIdk || isOther,
      vehicle_selection_source: p.model_unknown
        ? "fallback_model_unknown"
        : isIdk
          ? "fallback_variant_unknown"
          : isOther
            ? "other_variant"
            : "dropdown",
    }));
  }

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const next = () => setStep((s) => Math.min(STEPS, s + 1));
  const back = () => (step > 1 ? setStep((s) => s - 1) : nav("/"));

  // Persist draft on every change so a refresh mid-flow preserves photos + fields.
  useEffect(() => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* ignore */ }
  }, [form]);

  const submit = async () => {
    setSubmitting(true);
    try {
      // Convert numeric-string fields to actual numbers at the boundary.
      // Keeping them as strings in form state prevents the iOS Safari
      // "leading 0" / cursor-jump bug on numeric inputs.
      const payload = {
        ...form,
        year: parseInt(form.year, 10) || 0,
        km_driven: parseInt(form.km_driven, 10) || 0,
      };
      // Phase 8.1 — attach attribution payload if any was captured on landing.
      const attribution = buildAttributionSubmitPayload();
      if (attribution) payload.attribution = attribution;
      const { data } = await api.post("/vehicles/submit", payload);
      setVid(data.id);
      // Submission succeeded — clear draft + attribution now.
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      clearAttribution();
      setStep(6);
      setTimeout(() => setStep(7), 1200);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally { setSubmitting(false); }
  };

  // Numeric-text input handler: strips non-digits (preserves natural cursor),
  // no scientific notation, no negatives, no forced leading zeros.
  const onNumericChange = (key) => (e) => {
    const cleaned = e.target.value.replace(/[^\d]/g, "");
    // Strip a single leading zero so "05" → "5", but allow empty string.
    const normalized = cleaned.replace(/^0+(?=\d)/, "");
    update(key, normalized);
  };

  // Step 1 validation: brand + model + variant + year + km are ALL required.
  // Variant is now mandatory (previously optional). Fallback variant options
  // ("I don't know / Not sure", "Other Variant") still count as a valid selection.
  const canContinueStep1 = useMemo(() => {
    const yearNum = parseInt(form.year, 10);
    const kmNum = parseInt(form.km_driven, 10);
    return Boolean(
      form.make.trim() &&
      form.model.trim() &&
      form.variant.trim() &&
      yearNum >= 1990 && yearNum <= new Date().getFullYear() &&
      kmNum >= 0
    );
  }, [form.make, form.model, form.variant, form.year, form.km_driven]);

  return (
    <div className="min-h-screen pb-32" data-testid="sell-flow">
      <header className="px-6 pb-2 qd-pt-safe-header"><QLogo size={26} /></header>
      <div className="px-6 pt-3 max-w-2xl mx-auto">
        <StepHeader step={step} onBack={back} onHome={() => nav("/")} />

        {/* STEP 1 — Vehicle Basics (manual entry) */}
        {step === 1 && (
          <section data-testid="sell-step-1">
            <h1 className="qd-h1 text-3xl sm:text-4xl text-white mb-2" style={{ letterSpacing: "-0.02em" }}>Sell Your Car</h1>
            <p className="text-white/55 text-sm mb-5 max-w-md">Tell us about your vehicle. Our acquisition team handles the rest.</p>

            {/* Compact metrics row — database strength communicated in <2s.
                3 equal pills, mobile-first, stacks cleanly on small screens. */}
            <div
              data-testid="sell-metrics-row"
              className="mb-6 grid grid-cols-3 gap-2 sm:gap-3"
            >
              {[
                { value: "200+", label: "Models", testid: "sell-metric-models" },
                { value: "1500+", label: "Variants", testid: "sell-metric-variants" },
                { value: "<1 MIN", label: "Process", testid: "sell-metric-process" },
              ].map((m) => (
                <div
                  key={m.testid}
                  data-testid={m.testid}
                  className="relative rounded-md border border-white/10 bg-[#0E0E0E]/80 px-2 py-2.5 sm:px-3 sm:py-3 text-center overflow-hidden"
                >
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#E53935]"
                  />
                  <div className="text-white text-sm sm:text-lg font-semibold tracking-tight leading-tight">
                    {m.value}
                  </div>
                  <div className="text-white/45 text-[10px] sm:text-[11px] font-display tracking-[0.18em] uppercase mt-0.5">
                    {m.label}
                  </div>
                </div>
              ))}
            </div>

            <RegistrationPicker
              region={form.registration_region}
              registrationNumber={form.registration_number}
              onChangeRegion={(r) => update("registration_region", r)}
              onChangeRegistration={(v) => update("registration_number", v)}
            />

            <div className="grid grid-cols-2 gap-4">
              <FieldRow label="Brand">
                <BrandPicker
                  value={form.make}
                  onChange={setMake}
                />
              </FieldRow>
              <FieldRow label="Model">
                <SearchableDropdown
                  options={modelOptions}
                  value={form.model}
                  onChange={setModel}
                  placeholder="Select model"
                  disabled={!form.make || modelOptions.length === 0}
                  testid="sell-model"
                  emptyLabel="No models match"
                />
              </FieldRow>
            </div>

            <FieldRow label="Variant">
              <SearchableDropdown
                options={variantOptions}
                value={form.variant}
                onChange={setVariant}
                placeholder="Select variant"
                disabled={!form.model || variantOptions.length === 0}
                testid="sell-variant"
                emptyLabel="No variants match"
              />
            </FieldRow>

            <FieldRow label="Fuel Type">
              <ChipRow value={form.fuel_type} options={FUELS} onChange={(v) => update("fuel_type", v)} testid="fuel" />
            </FieldRow>

            <FieldRow label="Transmission">
              <ChipRow value={form.transmission} options={TRANS} onChange={(v) => update("transmission", v)} testid="trans" />
            </FieldRow>

            <div className="grid grid-cols-2 gap-4">
              <FieldRow label="Year">
                <select
                  data-testid="sell-year"
                  className="qd-input-block bg-[#0E0E0E] cursor-pointer appearance-none pr-7"
                  style={{
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M3 4.5L6 7.5L9 4.5' stroke='%23999' stroke-width='1.4' stroke-linecap='round'/%3E%3C/svg%3E\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 0.5rem center",
                    backgroundSize: "12px 12px",
                  }}
                  value={form.year}
                  onChange={(e) => update("year", e.target.value)}
                >
                  <option value="" className="bg-[#0E0E0E]">Select year</option>
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y} className="bg-[#0E0E0E]">{y}</option>
                  ))}
                </select>
              </FieldRow>
              <FieldRow label="Kilometers Driven">
                <input
                  data-testid="sell-km"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  className="qd-input-block"
                  placeholder="Enter KM"
                  value={form.km_driven}
                  onChange={onNumericChange("km_driven")}
                  maxLength={7}
                />
              </FieldRow>
            </div>

            <WizardFooter>
              <button
                data-testid="sell-continue-1"
                className="btn-primary w-full flex items-center justify-center gap-2"
                onClick={next}
                disabled={!canContinueStep1}
              >
                Continue <ArrowRight size={15} weight="bold" />
              </button>
              {!canContinueStep1 && (
                <div className="text-[11px] text-white/40 mt-2 text-center" data-testid="sell-step1-validation">
                  Brand, model, variant, year, and KM are required.
                </div>
              )}
            </WizardFooter>

            <div className="flex flex-wrap gap-2 mt-8">
              <span className="qd-pill"><ShieldCheck size={12} className="text-[#E55A5A]" weight="fill" /> Reviewed by our team</span>
              <span className="qd-pill"><Lightning size={12} className="text-[#E55A5A]" weight="fill" /> Direct acquisition</span>
            </div>
          </section>
        )}

        {/* STEP 2 — Ownership */}
        {step === 2 && (
          <section data-testid="sell-step-2">
            <h2 className="qd-h1 text-3xl text-white mb-8">Ownership</h2>

            <FieldRow label="Number of Owners">
              <ChipRow value={String(form.owners)} options={OWNER_OPTIONS} onChange={(v) => update("owners", v === "4+" ? 4 : Number(v))} testid="owners" />
            </FieldRow>

            <div className="mt-6">
              <label className="qd-label">Hypothecation Active?</label>
              <ChipRow value={form.hypothecation ? "yes" : "no"} options={HYPO_OPTIONS} onChange={(v) => update("hypothecation", v === "yes")} testid="hypo" />
            </div>

            <div className="qd-card p-5 mt-10 flex items-start gap-3">
              <ShieldCheck size={20} className="text-[#FF1E1E] mt-0.5" weight="fill" />
              <div>
                <div className="font-display text-sm text-white mb-1">Reviewed by our team</div>
                <div className="text-xs text-white/55">Verified manually. Never shared publicly.</div>
              </div>
            </div>
            <WizardFooter>
              <button data-testid="sell-continue-2" className="btn-primary w-full" onClick={next}>Continue</button>
            </WizardFooter>
          </section>
        )}

        {/* STEP 3 — Condition + Photos */}
        {step === 3 && (
          <section data-testid="sell-step-3">
            <h2 className="qd-h1 text-3xl text-white mb-8">Condition</h2>

            <div className="flex items-center justify-center mb-8">
              <ConditionRing score={form.condition_score} />
            </div>

            <FieldRow label="Condition Score">
              <input type="range" min={40} max={100} value={form.condition_score} onChange={(e) => update("condition_score", Number(e.target.value))} className="w-full accent-[#FF1E1E]" />
            </FieldRow>
            <FieldRow label="Accident History">
              <ChipRow value={form.accident_history} options={ACCIDENT_OPTIONS} onChange={(v) => update("accident_history", v)} testid="acc" />
            </FieldRow>
            <FieldRow label="Service History">
              <ChipRow value={form.service_history} options={SERVICE_OPTIONS} onChange={(v) => update("service_history", v)} testid="svc" />
            </FieldRow>

            <PhotosField photos={form.photos} onChange={(v) => update("photos", v)} />

            <WizardFooter>
              <button data-testid="sell-continue-3" className="btn-primary w-full" onClick={next}>Continue</button>
            </WizardFooter>
          </section>
        )}

        {/* STEP 4 — Intent */}
        {step === 4 && (
          <section data-testid="sell-step-4">
            <h2 className="qd-h1 text-3xl text-white mb-8">Your Goal</h2>

            <FieldRow label="What are you looking for?">
              <div className="flex flex-col gap-2" data-testid="customer-intent-options">
                {CUSTOMER_INTENTS.map((opt) => {
                  const selected = form.customer_intent === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`intent-${opt.v}`}
                      onClick={(e) => { update("customer_intent", opt.v); e.currentTarget.blur(); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 border text-left transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-[#FF1E1E]/40 ${
                        selected
                          ? "bg-[#FF1E1E]/10 border-[#FF1E1E]/60 text-white"
                          : "bg-[#0E0E0E] border-[#2A2A2A] text-white/75 hover:border-[#FF1E1E]/40 hover:text-white"
                      }`}
                    >
                      {/* Custom radio dot — visually consistent with the rest of the Q Drives palette */}
                      <span
                        className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0 ${
                          selected ? "border-[#FF1E1E]" : "border-white/30"
                        }`}
                        aria-hidden
                      >
                        {selected && <span className="w-1.5 h-1.5 rounded-full bg-[#FF1E1E]" />}
                      </span>
                      <span className="text-sm font-display normal-case leading-tight">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </FieldRow>

            {form.customer_intent === "other" && (
              <FieldRow label="Additional Details (Optional)">
                <input
                  data-testid="intent-note-input"
                  type="text"
                  maxLength={CUSTOMER_INTENT_NOTE_MAX}
                  className="qd-input-block"
                  placeholder="Tell us a bit more (optional)"
                  value={form.customer_intent_note}
                  onChange={(e) => update("customer_intent_note", e.target.value.slice(0, CUSTOMER_INTENT_NOTE_MAX))}
                />
                <div className="text-[10px] font-mono-data tracking-widest text-white/35 mt-1 text-right">
                  {form.customer_intent_note.length} / {CUSTOMER_INTENT_NOTE_MAX}
                </div>
              </FieldRow>
            )}

            <FieldRow label="Expected Price (₹)">
              <input
                data-testid="sell-expected-price"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                className="qd-input-block"
                placeholder="e.g. 850000"
                value={form.expected_price || ""}
                onChange={(e) => {
                  // Direct numeric input — replaces the slider so sellers can
                  // type the exact figure they want. Numeric keypad on mobile.
                  const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 9);
                  update("expected_price", digits === "" ? 0 : parseInt(digits, 10));
                }}
              />
              {form.expected_price > 0 && (
                <div className="text-[10px] font-mono-data tracking-widest text-white/40 mt-1">
                  ≈ ₹{(form.expected_price / 100000).toFixed(2)}L
                </div>
              )}
            </FieldRow>

            <WizardFooter>
              <button
                data-testid="sell-continue-4"
                className="btn-primary w-full disabled:opacity-40"
                onClick={next}
                disabled={!form.expected_price || form.expected_price < 50000}
              >Continue</button>
            </WizardFooter>
          </section>
        )}

        {/* STEP 5 — Review */}
        {step === 5 && (
          <section data-testid="sell-step-5">
            <h2 className="qd-h1 text-3xl text-white mb-6">Confirm & Submit</h2>

            <SummaryCard title="Vehicle" onEdit={() => setStep(1)}>
              <Row k="Make/Model" v={`${form.make} ${form.model}`} />
              <Row k="Variant" v={form.variant || "—"} />
              <Row k="Fuel" v={form.fuel_type} />
              <Row k="Transmission" v={form.transmission} />
              <Row k="Year" v={form.year} />
              <Row k="KM" v={`${(parseInt(form.km_driven, 10) || 0).toLocaleString()} km`} />
              <Row k="Registration" v={form.registration_number || "—"} />
            </SummaryCard>

            <SummaryCard title="Ownership" onEdit={() => setStep(2)}>
              <Row k="Owners" v={form.owners} />
              <Row k="Hypothecation" v={form.hypothecation ? "Yes" : "No"} />
            </SummaryCard>

            <SummaryCard title="Condition" onEdit={() => setStep(3)}>
              <Row k="Score" v={`${form.condition_score}%`} />
              <Row k="Accident" v={form.accident_history} />
              <Row k="Service" v={form.service_history} />
              <Row k="Photos" v={form.photos.length ? `${form.photos.length} attached` : "None"} />
            </SummaryCard>

            <SummaryCard title="Intent" onEdit={() => setStep(4)}>
              <Row k="Goal" v={CUSTOMER_INTENTS.find(i => i.v === form.customer_intent)?.label} />
              {form.customer_intent === "other" && form.customer_intent_note && (
                <Row k="Details" v={form.customer_intent_note} />
              )}
              <Row k="Expected" v={`₹${(form.expected_price / 100000).toFixed(1)}L`} />
            </SummaryCard>

            <WizardFooter>
              <button data-testid="sell-submit-btn" className="btn-primary w-full" onClick={submit} disabled={submitting}>
                {submitting ? "Submitting…" : "Submit Vehicle"}
              </button>
              <div className="text-[11px] text-white/40 mt-2 text-center">Reviewed manually by our acquisition team.</div>
            </WizardFooter>
          </section>
        )}

        {/* STEP 6 — Processing (brief) */}
        {step === 6 && (
          <section className="pt-8" data-testid="sell-step-6">
            <h2 className="qd-h1 text-2xl text-white mb-2">Processing</h2>
            <p className="text-sm text-white/55 mb-8">Securing your record.</p>
            <div className="space-y-3 max-w-sm w-full">
              {["Saving details", "Routing to acquisition team", "Preparing review"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-sm">
                  <CheckCircle size={16} weight="fill" className="text-[#C20505]" />
                  <span className="text-white/75">{t}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* STEP 7 — Success */}
        {step === 7 && (
          <section className="pt-6" data-testid="sell-step-7">
            <CheckCircle size={48} weight="fill" className="text-[#C20505] mb-5" />
            <h2 className="qd-h1 text-3xl text-white mb-2" style={{ letterSpacing: "-0.02em" }}>Vehicle Submitted</h2>
            <p className="text-sm text-white/60 mb-8 max-w-md">A Q Drives acquisition specialist is reviewing your vehicle.</p>

            <div className="mb-8">
              <div className="qd-label mb-3">Acquisition Pipeline</div>
              <PipelineStages active={1} />
            </div>

            <div className="qd-card p-5 mb-8" style={{ borderColor: "rgba(225,29,29,0.35)" }}>
              <div className="qd-label mb-2" style={{ color: "#E55A5A" }}>Priority Review</div>
              <p className="text-[13px] text-white/65 mb-4 leading-relaxed">
                Speak directly with our acquisition team to fast-track your review.
              </p>
              <a
                href={waLink(`Hi Q Drives, I just submitted my ${form.make} ${form.model}${form.registration_number ? ` (${form.registration_number})` : ""}. Requesting priority review.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-priority-btn"
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
              >
                <WhatsappLogo size={16} weight="fill" /> Connect on WhatsApp
              </a>
            </div>

            <div className="flex gap-3">
              <button data-testid="sell-track-btn" className="btn-primary flex-1" onClick={() => nav("/activity")}>Track Submission</button>
              <button data-testid="sell-return-home" className="btn-secondary flex-1" onClick={() => nav("/")}>Return Home</button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="mb-5">
      <label className="qd-label">{label}</label>
      {children}
    </div>
  );
}

function ChipRow({ value, options, onChange, testid }) {
  const items = options.map((o) => (typeof o === "string" ? { v: o, l: o } : o));
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((o) => (
        <button
          key={o.v}
          data-testid={`${testid}-chip-${o.v}`}
          className={`qd-chip ${value === o.v ? "active" : ""}`}
          onClick={() => onChange(o.v)}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BrandPicker — searchable dropdown matching the qd-input styling.
//
// Design notes:
//   • The trigger is identical in height/width to qd-input-block so the form
//     layout never shifts when this replaces a plain <input>.
//   • The menu is `position: absolute` and overlays content — no page push,
//     no layout shift, no horizontal scroll (per requirement §8).
//   • Mobile: tap to open, tap to pick. Long brand names truncate gracefully.
//   • Free typing is still possible — if the user types something not in the
//     list, it's accepted on Enter / blur (treated like "Other → custom").
// ---------------------------------------------------------------------------
function BrandPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = React.useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return BRANDS;
    return BRANDS.filter((b) => b.toLowerCase().includes(q));
  }, [query]);

  const pick = (brand) => {
    onChange(brand);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      {/* Trigger: matches qd-input-block height/padding for zero layout shift */}
      <button
        type="button"
        data-testid="sell-make"
        onClick={() => { setOpen(true); setTimeout(() => wrapRef.current?.querySelector('input[data-testid="brand-search-input"]')?.focus(), 50); }}
        className="qd-input-block text-left flex items-center justify-between cursor-pointer w-full"
      >
        <span className={`truncate ${value ? "text-white" : "text-white/40"}`}>
          {value || "Select brand"}
        </span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="shrink-0 ml-2 text-white/40">
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 bg-[#0E0E0E] border border-[#2A2A2A] shadow-2xl max-h-[260px] flex flex-col"
          data-testid="brand-dropdown"
        >
          <input
            type="text"
            data-testid="brand-search-input"
            autoComplete="off"
            placeholder="Search brand…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="bg-[#0A0A0A] border-b border-[#2A2A2A] text-white text-sm py-2 px-3 outline-none focus:border-[#FF1E1E]"
          />
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-[12px] text-white/40">
                No match. Tap to keep typing for custom value.
              </div>
            ) : (
              filtered.map((b) => (
                <button
                  key={b}
                  type="button"
                  data-testid={`brand-opt-${b}`}
                  onClick={() => pick(b)}
                  className={`block w-full text-left px-3 py-2.5 text-[13px] active:bg-[#FF1E1E]/15 hover:bg-white/5 ${
                    value === b ? "text-[#FF1E1E]" : "text-white"
                  }`}
                >
                  {b}
                </button>
              ))
            )}
            {/* "Type-in custom" fallback when user's query doesn't match any preset */}
            {query.trim() && !BRANDS.some((b) => b.toLowerCase() === query.trim().toLowerCase()) && (
              <button
                type="button"
                data-testid="brand-opt-custom"
                onClick={() => pick(query.trim())}
                className="block w-full text-left px-3 py-2.5 text-[13px] text-white/55 border-t border-[#2A2A2A] hover:bg-white/5"
              >
                Use &quot;{query.trim()}&quot;
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}



function ConditionRing({ score }) {
  const r = 60;
  const c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  const label = score >= 85 ? "Excellent" : score >= 70 ? "Strong" : score >= 55 ? "Fair" : "Needs Attention";
  return (
    <div className="relative" style={{ width: 150, height: 150 }}>
      <svg width={150} height={150}>
        <circle cx={75} cy={75} r={r} stroke="#1F1F1F" strokeWidth="6" fill="none" />
        <circle
          cx={75} cy={75} r={r}
          stroke="#C20505" strokeWidth="6" fill="none"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
          transform="rotate(-90 75 75)"
          style={{ transition: "stroke-dashoffset 0.5s" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-3xl font-semibold text-white">{score}%</div>
        <div className="text-[11px] text-white/55 mt-1">{label}</div>
      </div>
    </div>
  );
}

function SummaryCard({ title, onEdit, children }) {
  return (
    <div className="py-4 border-b border-white/8">
      <div className="flex items-center justify-between mb-2">
        <div className="qd-label" style={{ color: "rgba(255,255,255,0.7)" }}>{title}</div>
        <button data-testid={`edit-${title.toLowerCase().replace(/\s+/g, '-')}`} className="text-white/45 text-[11px] hover:text-[#E55A5A]" onClick={onEdit}>Edit</button>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between text-[13px]">
      <span className="text-white/45">{k}</span>
      <span className="text-white/90">{v}</span>
    </div>
  );
}

const PIPELINE = ["Submitted", "Under Review", "Buyer Review", "Offer Stage", "Closed"];

function PipelineStages({ active = 0 }) {
  return (
    <div className="flex items-center gap-1.5">
      {PIPELINE.map((stage, i) => {
        const reached = i <= active;
        return (
          <React.Fragment key={stage}>
            <div className="flex-1 min-w-0">
              <div className="h-[3px]" style={{ background: reached ? "#C20505" : "rgba(255,255,255,0.08)" }} />
              <div className={`text-[10px] mt-2 truncate ${reached ? "text-white/85" : "text-white/35"}`} style={{ letterSpacing: "0.02em" }}>
                {stage}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * PhotosField — manual upload with per-photo real previews + retry on failure.
 *
 * Internal item: { id, localUrl, remoteUrl, status: 'uploading'|'done'|'failed', blob }
 *
 * Contract with parent: only `remoteUrl` of items with status='done' is propagated
 * to parent.photos (List[str]). This keeps the backend payload unchanged.
 *
 * Persistence: parent persists `form.photos` (the successful URLs only) to localStorage,
 * so refresh restores them. In-flight uploading items are intentionally not persisted
 * — those need to be reuploaded from a fresh file pick anyway.
 */
function PhotosField({ photos, onChange }) {
  const fileInputRef = React.useRef(null);
  const cameraInputRef = React.useRef(null);
  const MAX = 8;
  const MAX_W = 1600;
  const QUALITY = 0.82;

  // Hydrate items from incoming `photos` (parent state); add any in-flight uploads on top.
  const [items, setItems] = useState(() =>
    (photos || []).map((url, i) => ({
      id: `restored-${i}-${url.slice(0, 20)}`,
      localUrl: url,    // works for both data: URLs and /api/files/* signed URLs
      remoteUrl: url,
      status: "done",
      blob: null,
    }))
  );

  // Push successful URLs back up to parent whenever items change.
  useEffect(() => {
    const ok = items.filter((it) => it.status === "done" && it.remoteUrl).map((it) => it.remoteUrl);
    // Avoid infinite loop: only push if changed.
    const same = ok.length === (photos || []).length && ok.every((u, i) => u === photos[i]);
    if (!same) onChange(ok);
  }, [items]);

  // Clean up local blob URLs when component unmounts.
  useEffect(() => {
    return () => {
      items.forEach((it) => {
        if (it.localUrl && it.localUrl.startsWith("blob:")) {
          try { URL.revokeObjectURL(it.localUrl); } catch { /* ignore */ }
        }
      });
    };
  }, []);

  const compress = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, MAX_W / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            if (!blob) { reject(new Error("Encode failed")); return; }
            resolve(blob);
          },
          "image/jpeg",
          QUALITY,
        );
      };
      img.onerror = () => reject(new Error("Image decode failed"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("File read failed"));
    reader.readAsDataURL(file);
  });

  const uploadOne = async (blob, attempt = 0) => {
    const fd = new FormData();
    fd.append("file", blob, "photo.jpg");
    const token = localStorage.getItem("qdrives_token");
    try {
      const { data } = await api.post("/uploads/photo", fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (data?.url) return data.url;
      throw new Error("No URL in response");
    } catch (err) {
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 600 * Math.pow(2, attempt)));
        return uploadOne(blob, attempt + 1);
      }
      throw err;
    }
  };

  const startUploadFor = async (id, blob) => {
    try {
      const url = await uploadOne(blob);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status: "done", remoteUrl: url } : it)));
    } catch (err) {
      console.error("Photo upload failed:", err);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status: "failed" } : it)));
    }
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const room = MAX - items.length;
    const incoming = Array.from(files).slice(0, room);
    if (files.length > room) toast(`Max ${MAX} photos. Extra ignored.`);

    for (const f of incoming) {
      if (!f.type.startsWith("image/")) { toast.error("Only image files"); continue; }
      let blob;
      try {
        blob = await compress(f);
      } catch (err) {
        toast.error("Could not read photo — try another.");
        continue;
      }
      const localUrl = URL.createObjectURL(blob);
      const id = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setItems((prev) => [...prev, { id, localUrl, remoteUrl: null, status: "uploading", blob }]);
      startUploadFor(id, blob);
    }
  };

  const retry = (id) => {
    const item = items.find((it) => it.id === id);
    if (!item || !item.blob) return;
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status: "uploading" } : it)));
    startUploadFor(id, item.blob);
  };

  const remove = (id) => {
    setItems((prev) => {
      const target = prev.find((it) => it.id === id);
      if (target?.localUrl?.startsWith("blob:")) {
        try { URL.revokeObjectURL(target.localUrl); } catch { /* ignore */ }
      }
      return prev.filter((it) => it.id !== id);
    });
  };

  const uploadingCount = items.filter((it) => it.status === "uploading").length;
  const failedCount = items.filter((it) => it.status === "failed").length;

  return (
    <div className="mb-5" data-testid="sell-photos-field">
      <label className="qd-label">Photos · {items.length}/{MAX}</label>

      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />

      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          type="button"
          data-testid="sell-photo-camera"
          onClick={() => cameraInputRef.current?.click()}
          disabled={items.length >= MAX}
          className="aspect-square bg-[#0E0E0E] border border-[#2A2A2A] flex flex-col items-center justify-center gap-1 text-xs text-white/70 hover:border-[#FF1E1E]/50 hover:text-white disabled:opacity-50 transition-colors"
        >
          <Camera size={20} weight="bold" />
          <span>Camera</span>
        </button>
        <button
          type="button"
          data-testid="sell-photo-gallery"
          onClick={() => fileInputRef.current?.click()}
          disabled={items.length >= MAX}
          className="aspect-square bg-[#0E0E0E] border border-[#2A2A2A] flex flex-col items-center justify-center gap-1 text-xs text-white/70 hover:border-[#FF1E1E]/50 hover:text-white disabled:opacity-50 transition-colors"
        >
          <ImageSquare size={20} weight="bold" />
          <span>Gallery</span>
        </button>
      </div>

      {(uploadingCount > 0 || failedCount > 0) && (
        <div className="text-[11px] mb-2 space-x-3">
          {uploadingCount > 0 && <span className="text-white/55">Uploading {uploadingCount}…</span>}
          {failedCount > 0 && <span className="text-[#E55A5A]">{failedCount} failed — tap to retry</span>}
        </div>
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {items.map((it, i) => (
            <div
              key={it.id}
              className="relative aspect-square bg-[#0E0E0E] border border-white/8 overflow-hidden group"
              data-testid={`sell-photo-tile-${i}`}
            >
              <img
                src={it.localUrl || it.remoteUrl}
                alt={`Vehicle photo ${i + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />

              {it.status === "uploading" && (
                <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                  <div className="text-[10px] text-white/85 tracking-wide">UPLOADING…</div>
                </div>
              )}
              {it.status === "failed" && (
                <button
                  type="button"
                  data-testid={`sell-photo-retry-${i}`}
                  onClick={() => retry(it.id)}
                  className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-1 text-[#E55A5A]"
                >
                  <ArrowClockwise size={20} weight="bold" />
                  <span className="text-[10px] tracking-wide">RETRY</span>
                </button>
              )}

              {i === 0 && it.status === "done" && (
                <span className="absolute top-1 left-1 bg-[#FF1E1E] text-white text-[9px] tracking-wide px-1.5 py-[1px]">COVER</span>
              )}
              <button
                type="button"
                data-testid={`sell-photo-remove-${i}`}
                onClick={() => remove(it.id)}
                className="absolute top-1 right-1 bg-black/75 text-white/90 p-0.5 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove photo"
              >
                <X size={11} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-white/35 mt-2">First successful photo becomes the cover. Failed uploads can be retried.</div>
    </div>
  );
}
